import { useRef, useCallback, useEffect } from 'preact/compat';

export const MODAL_OVERLAY = Symbol.for('oj-modal-overlay');

export type BackdropElement = HTMLDivElement & {
  [MODAL_OVERLAY]?: boolean;
};

export type UseModalProps = Readonly<{
  /**
   * Determines whether the Modal is open.
   */
  isOpen: boolean;
  /**
   * On backdrop click callback.
   */
  onBackdropClick?: (event: MouseEvent) => void;
}>;

/**
 *
 * @param props
 */
export const useModal = (props: UseModalProps) => {
  const { isOpen, onBackdropClick: onBackdropClickProp } = props;

  const backdropRef = useRef<HTMLDivElement>(null);

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const onMouseDown = useCallback((event: MouseEvent) => {
    if (backdropRef.current === event.target) {
      mouseDownTarget.current = event.target;
    }
  }, []);

  const onBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (backdropRef.current === event.target) {
        event.stopPropagation();
        // event should originate on the same target
        if (mouseDownTarget.current === event.target) {
          onBackdropClickProp?.(event);
        }
      }
    },
    [onBackdropClickProp]
  );

  useEffect(() => {
    const modal = backdropRef.current as BackdropElement;

    if (modal) {
      modal.addEventListener('mousedown', onMouseDown);
      modal.addEventListener('click', onBackdropClick);
      modal[MODAL_OVERLAY] = true;

      return () => {
        modal.removeEventListener('mousedown', onMouseDown);
        modal.removeEventListener('click', onBackdropClick);
        delete modal[MODAL_OVERLAY];
      };
    }

    return () => {};
  }, [isOpen, onMouseDown, onBackdropClick]);

  return {
    backdropRef
  };
};

export default useModal;
